import { DatePipe } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { map, Observable, startWith } from 'rxjs';
import { JobService } from '../../job/services/job.service';
import { QuoteService } from '../../quote/services/quote.service';
import { Customer } from '../../shared/models/customer.interface';
import { Job } from '../../shared/models/job.interface';
import { MonthType } from '../../shared/models/month.type.interface';
import { FileService } from '../../shared/services/file.service';
import { AbstractReportComponent } from '../abstract.report.component';
import { JobCostResult, JobCost } from '../models/job.cost.interface';
import { ReportService } from '../services/report.service';

export interface JobCostParameterData {
  jobCost: JobCost;
  validation: string;

  customers: Customer[];
  jobs: Job[];
  startDate: Date;
  endDate: Date;
}

@Component({
    selector: 'app-job-cost',
    templateUrl: './job-cost.component.html',
    styleUrls: ['./job-cost.component.scss']
})
/** jobCost component*/
export class JobCostComponent extends AbstractReportComponent {

  report: JobCostResult = null;
  parameters: JobCost = {
    startDate: '',
    endDate: ''
  } as JobCost;

  customer: Customer;
  job: Job;

  customers: Customer[];
  jobs: Job[];
  startDate = new Date();
  endDate = new Date();

    /** jobCost ctor */
  constructor(@Inject(LOCALE_ID) locale: string,
    deviceDetectorService: DeviceDetectorService, private datepipe: DatePipe,
    dialog: MatDialog, private jobService: JobService, private quoteService: QuoteService,
    private reportService: ReportService, private fileService: FileService) {
    super(locale, dialog, deviceDetectorService);

    this.startDate.setMonth(this.endDate.getMonth() - 1);

    this.jobService.getJobList()
      .subscribe({
        next: (data) => {
          this.jobs = data;

          if (this.parametersReady)
            this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getCustomers()
      .subscribe({
        next: (data) => {
          this.customers = data;

          if (this.parametersReady)
            this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.setupDates();
  }

  get parametersReady(): boolean {
    return this.customers != undefined && this.jobs != undefined;
  }

  renderReport() {
    this.getReportFormat()
      .subscribe(formatData => {
        if (formatData == null) return;

        this.isRequesting = true;
        this.reportService.getJobCostExport(this.parameters, formatData.format, formatData.locale)
          .subscribe({
            next: (data) => {
              this.isRequesting = false;
              this.fileService.downloadFile(data, 'JobCost' + formatData.extension);
            },
            error: (error: string) =>this.onError(error)
          });
      });
  }

  showParameters() {
    const dialogRef = this.dialog.open(JobCostParameterDialog, {
      width: '550px',
      autoFocus: false,
      data: {
        jobCost: (<any>Object).assign({}, this.parameters),

        jobs: this.jobs,
        customers: this.customers,
        startDate: this.startDate,
        endDate: this.endDate
      } as JobCostParameterData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.parameters = result.jobCost as JobCost;
        this.startDate = result.startDate;
        this.endDate = result.endDate;

        this.parameters.startDate = this.datepipe.transform(this.startDate, 'yyyy-MM-ddT00:00:00');
        this.parameters.endDate = this.datepipe.transform(this.endDate, 'yyyy-MM-ddT00:00:00');

        this.customer = this.customers.find(s => s.customer_ID == this.parameters.customer_ID);
        this.job = this.jobs.find(s => s.quote_ID == this.parameters.quote_ID);

        this.reportService.getJobCostReport(this.parameters)
          .subscribe({
            next: (data) => {
              this.report = data;              
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }
}

@Component({
  selector: 'job-cost.parameter.dialog',
  templateUrl: 'job-cost.parameter.dialog.html',
})
export class JobCostParameterDialog implements OnInit {

  customerControl = new FormControl();
  jobControl = new FormControl();

  filteredCustomers: Observable<Customer[]>;
  filteredJobs: Observable<Job[]>;

  errors: string = '';

  constructor(
    public dialogRef: MatDialogRef<JobCostParameterDialog>,
    @Inject(MAT_DIALOG_DATA) public data: JobCostParameterData) { }

  ngOnInit() {
    if (this.data.jobCost.customer_ID) {
      let customerName = this.data.customers.find(c => c.customer_ID == this.data.jobCost.customer_ID)?.customer_BusinessName ?? '';
      this.customerControl.setValue(customerName);
    }

    if (this.data.jobCost.quote_ID) {
      let jobName = this.data.jobs.find(c => c.quote_ID == this.data.jobCost.quote_ID)?.quote_Name ?? '';
      this.jobControl.setValue(jobName);
    }

    this.filteredCustomers = this.customerControl.valueChanges
      .pipe(
        startWith(''),
        map(value =>
          this._filterCustomer(value)
        )
    );

    this.filteredJobs = this.jobControl.valueChanges
      .pipe(
        startWith(''),
        map(value =>
          this._filterJob(value)
        )
      );

    this.filteredCustomers.subscribe(value => {
      let customer = value.find(c => c.customer_BusinessName.toUpperCase() == this.customerControl.value?.toUpperCase());

      let customerId = customer?.customer_ID;

      // refresh jobs when customer changes
      if (this.data.jobCost.customer_ID != customerId) {
        this.data.jobCost.customer_ID = customerId;

        let job = this.data.jobs.find(j => j.quote_ID == this.data.jobCost.quote_ID);
        if (job?.customer_ID != customerId ||
          (job?.customer_ID == undefined && customerId == undefined)) {
          // limit jobs to current customer

          this.jobControl.setValue('');
        }

      }
    });

    this.filteredJobs.subscribe(value => {
      let job = value.find(c => c.quote_Name.toUpperCase() == this.jobControl.value?.toUpperCase());

      this.data.jobCost.quote_ID = job?.quote_ID;
    });

  }

  private _filterCustomer(value: string): Customer[] {
    if (this.data.customers == null) return new Array();

    const filterValue = value.toLowerCase();
    return this.data.customers.filter(option => option.customer_BusinessName.toLowerCase().indexOf(filterValue) === 0);
  }

  private _filterJob(value: string): Job[] {
    if (this.data.jobs == null) return new Array();

    const filterValue = value.toLowerCase();
    return this.data.jobs.filter(option =>
      (this.data.jobCost.customer_ID == undefined || option.customer_ID == this.data.jobCost.customer_ID) &&
      option.quote_Name.toLowerCase().indexOf(filterValue) === 0);
  }

  runReport(form: NgForm): void {
    this.errors = '';

    if (form.valid) {
      this.dialogRef.close(this.data);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
